import { useMemo, useState } from 'react';

import {
  useAccountingEntries,
  useCancelAccountingEntry,
  useCreateManualAccountingEntry,
  usePostAccountingEntry,
  useUpdateManualAccountingEntry,
} from '../../../hooks/useSupplierLedger';
import { INITIAL_ACCOUNTING_FILTERS } from '../constants/accountingEntriesConstants';
import {
  buildAccountingLedgerRows,
  extractAccountingErrorMessage,
} from '../utils/accountingEntriesUtils';
import useClientPagination from './useClientPagination';

const useAccountingEntriesController = () => {
  const [activeTab, setActiveTab] = useState('entries');
  const [filters, setFilters] = useState(INITIAL_ACCOUNTING_FILTERS);
  const [editingEntry, setEditingEntry] = useState(null);
  const [viewEntry, setViewEntry] = useState(null);
  const [postEntry, setPostEntry] = useState(null);
  const [cancelEntry, setCancelEntry] = useState(null);
  const [error, setError] = useState('');

  const apiFilters = useMemo(
    () => Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== '')),
    [filters],
  );
  const { data: entries = [], isLoading } = useAccountingEntries(apiFilters);
  const createManual = useCreateManualAccountingEntry();
  const updateManual = useUpdateManualAccountingEntry();
  const postAccountingEntry = usePostAccountingEntry();
  const cancelAccountingEntry = useCancelAccountingEntry();
  const entryPagination = useClientPagination(entries);
  const ledgerRows = useMemo(() => buildAccountingLedgerRows(entries), [entries]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const saveEntry = async (payload) => {
    if (editingEntry?.uuid) {
      await updateManual.mutateAsync({ id: editingEntry.uuid, data: payload });
    } else {
      await createManual.mutateAsync(payload);
    }
  };

  const handlePost = async () => {
    try {
      await postAccountingEntry.mutateAsync(postEntry.uuid);
      setPostEntry(null);
    } catch (err) {
      setError(extractAccountingErrorMessage(err));
    }
  };

  const handleCancel = async () => {
    try {
      await cancelAccountingEntry.mutateAsync(cancelEntry.uuid);
      setCancelEntry(null);
    } catch (err) {
      setError(extractAccountingErrorMessage(err));
    }
  };

  return {
    activeTab,
    cancelEntry,
    clearError: () => setError(''),
    closeCancelDialog: () => setCancelEntry(null),
    closeEditor: () => setEditingEntry(null),
    closePostDialog: () => setPostEntry(null),
    closeViewer: () => setViewEntry(null),
    editingEntry,
    entries,
    entryPagination,
    error,
    filters,
    handleCancel,
    handleFilterChange,
    handlePost,
    isCancelling: cancelAccountingEntry.isPending,
    isLoading,
    isPosting: postAccountingEntry.isPending,
    isSaving: createManual.isPending || updateManual.isPending,
    ledgerRows,
    openEditor: setEditingEntry,
    openViewer: setViewEntry,
    postEntry,
    saveEntry,
    setActiveTab,
    setCancelEntry,
    setPostEntry,
    viewEntry,
  };
};

export default useAccountingEntriesController;
